



import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import socket from '../Components/socket';
import axios from 'axios';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import {
  BsHeadset, BsSend, BsMic, BsPlusCircle, BsTelephone,
  BsMicMute, BsXCircle, BsList, BsCheckAll,
  BsPlayFill, BsFillTelephoneFill, BsPaperclip, BsTrash, BsShieldCheck
} from 'react-icons/bs';
import './SupportChat.css';

const beckendLocalApiUrl = process.env.REACT_APP_BACKEND_LOCAL_API
const beckendLiveApiUrl = process.env.REACT_APP_BACKEND_LIVE_API 
 const baseUrl = "https://backend.mplbets.com";

const SupportChat = () => {
  const history = useHistory();
  const access_token = localStorage.getItem('token');

  // State Variables
  const [currentUser, setCurrentUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [typing, setTyping] = useState(false);
  const [isOpponentTyping, setIsOpponentTyping] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // New ticket modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSubject, setNewSubject] = useState('Profile');
  const [newPriority, setNewPriority] = useState('medium');

  // Media upload & recording state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceDuration, setVoiceDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordIntervalRef = useRef(null);

  // WebRTC Call state
  const [callActive, setCallActive] = useState(false);
  const [callState, setCallState] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [incomingCallData, setIncomingCallData] = useState(null);
  const [currentCallId, setCurrentCallId] = useState(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);

  const messagesEndRef = useRef(null);

  // 1. Fetch profile & tickets
  useEffect(() => {
    if (!access_token) {
      toast.error('Please login first.');
      history.push('/login');
      return;
    }

    const headers = { Authorization: `Bearer ${access_token}` };

    axios.get(`${baseUrl}/me`, { headers })
      .then(res => {
        setCurrentUser(res.data);
      })
      .catch(() => {
        localStorage.removeItem('token');
        history.push('/login');
      });

    fetchTickets();

    socket.on('support:chat:updated', (updatedChat) => {
      setTickets(prev => prev.map(t => t._id === updatedChat._id ? { ...t, ...updatedChat } : t));
      if (activeTicket && activeTicket._id === updatedChat._id) {
        setActiveTicket(prev => ({ ...prev, ...updatedChat }));
      }
    });

    return () => {
      socket.off('support:chat:updated');
    };
  }, []);

  const fetchTickets = () => {
    setLoadingTickets(true);
    axios.get(`${baseUrl}/chat/support/list?limit=50`, {
      headers: { Authorization: `Bearer ${access_token}` }
    })
      .then(res => {
        setTickets(res.data.results || []);
        if (res.data.results && res.data.results.length > 0) {
          selectTicket(res.data.results[0]);
        }
        setLoadingTickets(false);
      })
      .catch(() => {
        setLoadingTickets(false);
      });
  };

  // 2. Select Ticket & Join Room
  const selectTicket = (ticket) => {
    setActiveTicket(ticket);
    setMessages([]);
    setLoadingMessages(true);
    setIsSidebarOpen(false);

    axios.get(`${baseUrl}/chat/support/${ticket._id}/messages?limit=100`, {
      headers: { Authorization: `Bearer ${access_token}` }
    })
      .then(res => {
        setMessages(res.data.results || []);
        setLoadingMessages(false);
        setTimeout(scrollToBottom, 100);
      })
      .catch(() => {
        setLoadingMessages(false);
      });

    socket.emit('support:join', { chatId: ticket._id, token: access_token });
  };

  // 3. Socket real-time messaging, typing, seen receipts
  useEffect(() => {
    if (!activeTicket) return;

    socket.on('support:message', (msg) => {
      if (msg.chatId === activeTicket._id) {
        setMessages(prev => {
          if (prev.find(m => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
        setTimeout(scrollToBottom, 100);

        socket.emit('support:seen', { chatId: activeTicket._id });
      }
    });

    socket.on('support:seen', (data) => {
      if (data.chatId === activeTicket._id) {
        setMessages(prev => prev.map(m => m.senderType !== 'User' ? m : { ...m, seen: true, seenAt: new Date() }));
      }
    });

    socket.on('support:typing', (data) => {
      if (data.chatId === activeTicket._id && data.userType !== 'User') {
        setIsOpponentTyping(data.isTyping);
      }
    });

    socket.on('support:status', (data) => {
      if (data.chat.id === activeTicket._id || data.chat._id === activeTicket._id) {
        setActiveTicket(data.chat);
        if (data.message) {
          setMessages(prev => [...prev, data.message]);
          setTimeout(scrollToBottom, 100);
        }
      }
    });

    socket.on('support:call:ringing', (data) => {
      setIncomingCallData(data);
      setCallState('ringing');
      setCallActive(true);
    });

    socket.on('support:call:action', (data) => {
      if (data.status === 'connected') {
        setCallState('connected');
        startCallTimer();
      } else if (['rejected', 'completed', 'missed'].includes(data.status)) {
        endCallCleanUp();
      }
    });

    socket.on('support:call:signal', async (data) => {
      if (!peerConnectionRef.current) return;
      const { signalData } = data;
      if (signalData.type === 'offer') {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(signalData.offer));
        const answer = await peerConnectionRef.current.createAnswer();
        await peerConnectionRef.current.setLocalDescription(answer);
        socket.emit('support:call:signal', {
          chatId: activeTicket._id,
          signalData: { type: 'answer', answer }
        });
      } else if (signalData.type === 'answer') {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(signalData.answer));
      } else if (signalData.type === 'candidate') {
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(signalData.candidate));
        } catch (e) {
          console.error('Error adding ICE candidate:', e);
        }
      }
    });

    return () => {
      socket.off('support:message');
      socket.off('support:seen');
      socket.off('support:typing');
      socket.off('support:status');
      socket.off('support:call:ringing');
      socket.off('support:call:action');
      socket.off('support:call:signal');
    };
  }, [activeTicket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 4. Send messages
  const handleSendMessage = () => {
    if (!inputText.trim() && !selectedFile) return;

    const payload = {
      chatId: activeTicket._id,
      message: inputText.trim(),
      messageType: selectedFile ? selectedFile.type : 'text',
      attachment: selectedFile ? selectedFile.url : null
    };

    socket.emit('support:message', payload);
    setInputText('');
    setSelectedFile(null);
  };

  const handleTextInputChange = (e) => {
    setInputText(e.target.value);
    if (!typing) {
      setTyping(true);
      socket.emit('support:typing', { chatId: activeTicket._id, isTyping: true });
    }

    clearTimeout(window.typingTimeout);
    window.typingTimeout = setTimeout(() => {
      setTyping(false);
      socket.emit('support:typing', { chatId: activeTicket._id, isTyping: false });
    }, 2000);
  };

  // 5. File upload handler
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadingFile(true);

    axios.post(`${baseUrl}/chat/support/upload`, formData, {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'multipart/form-data'
      }
    })
      .then(res => {
        let fileType = 'file';
        if (file.type.startsWith('image/')) fileType = 'image';

        setSelectedFile({
          url: res.data.url,
          type: fileType,
          name: file.name
        });
        setUploadingFile(false);
      })
      .catch(() => {
        toast.error('File upload failed.');
        setUploadingFile(false);
      });
  };

  // 6. Voice Message Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      audioChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setVoiceBlob(audioBlob);
      };

      mediaRecorder.start();
      setRecording(true);
      setVoiceDuration(0);

      recordIntervalRef.current = setInterval(() => {
        setVoiceDuration(prev => prev + 1);
      }, 1000);
    } catch (e) {
      toast.error('Microphone permission denied or unavailable.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      clearInterval(recordIntervalRef.current);
      setRecording(false);

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setVoiceBlob(null);
    setVoiceDuration(0);
  };

  const sendVoiceMessage = () => {
    if (!voiceBlob) return;

    const file = new File([voiceBlob], 'voice-message.webm', { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', file);

    setUploadingFile(true);

    axios.post(`${baseUrl}/chat/support/upload`, formData, {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'multipart/form-data'
      }
    })
      .then(res => {
        socket.emit('support:message', {
          chatId: activeTicket._id,
          message: '',
          messageType: 'voice',
          attachment: res.data.url
        });
        setVoiceBlob(null);
        setVoiceDuration(0);
        setUploadingFile(false);
      })
      .catch(() => {
        toast.error('Failed to upload voice message.');
        setUploadingFile(false);
      });
  };

  // 7. WebRTC Call signaling
  const initializePeerConnection = () => {
    const configuration = {
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    };

    const pc = new RTCPeerConnection(configuration);

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit('support:call:signal', {
          chatId: activeTicket._id,
          signalData: { type: 'candidate', candidate: event.candidate }
        });
      }
    };

    pc.ontrack = (event) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = event.streams[0];
      }
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    peerConnectionRef.current = pc;
  };

  const startVoiceCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      setCallActive(true);
      setCallState('calling');

      initializePeerConnection();

      socket.emit('support:call:initiate', {
        chatId: activeTicket._id,
        callerId: currentUser._id,
        receiverId: activeTicket.assignedAgentId
      });

      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);
      socket.emit('support:call:signal', {
        chatId: activeTicket._id,
        signalData: { type: 'offer', offer }
      });
    } catch (e) {
      toast.error('Could not access microphone for voice call.');
    }
  };

  const acceptCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      setCallState('connected');
      initializePeerConnection();

      socket.emit('support:call:action', {
        chatId: incomingCallData.chatId,
        callId: incomingCallData.callId,
        action: 'accept'
      });

      startCallTimer();
    } catch (e) {
      toast.error('Could not access microphone to accept call.');
      rejectCall();
    }
  };

  const rejectCall = () => {
    if (incomingCallData) {
      socket.emit('support:call:action', {
        chatId: incomingCallData.chatId,
        callId: incomingCallData.callId,
        action: 'reject'
      });
    }
    endCallCleanUp();
  };

  const hangupCall = () => {
    socket.emit('support:call:action', {
      chatId: activeTicket._id,
      callId: currentCallId || (incomingCallData && incomingCallData.callId),
      action: 'hangup'
    });
    endCallCleanUp();
  };

  const endCallCleanUp = () => {
    setCallActive(false);
    setCallState('');
    setIncomingCallData(null);
    setCurrentCallId(null);
    setIsMuted(false);
    clearInterval(callTimerRef.current);
    setCallDuration(0);

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const startCallTimer = () => {
    setCallDuration(0);
    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const formatDuration = (sec) => {
    const min = Math.floor(sec / 60);
    const remainingSec = sec % 60;
    return `${min.toString().padStart(2, '0')}:${remainingSec.toString().padStart(2, '0')}`;
  };

  // 8. Create a new support ticket
  const handleCreateTicket = () => {
    axios.post(`${baseUrl}/chat/support/create`, {
      subject: newSubject,
      priority: newPriority
    }, {
      headers: { Authorization: `Bearer ${access_token}` }
    })
      .then(res => {
        setShowCreateModal(false);
        toast.success('Support ticket created.');
        fetchTickets();
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Failed to create ticket.');
      });
  };

  // 9. Close current ticket
  const handleCloseTicket = () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;
    axios.post(`${baseUrl}/chat/support/${activeTicket._id}/close`, {}, {
      headers: { Authorization: `Bearer ${access_token}` }
    })
      .then(res => {
        toast.success('Ticket closed.');
        fetchTickets();
      })
      .catch(() => {
        toast.error('Failed to close ticket.');
      });
  };

  return (
    <div className="support-chat-page-container pt-5 mt-4">
      <div className={`support-chat-wrapper ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        <ToastContainer theme="light" />

        <audio ref={remoteAudioRef} autoPlay />

        {isSidebarOpen && (
          <div className="sidebar-backdrop" onClick={() => setIsSidebarOpen(false)}></div>
        )}

        {/* LEFT SIDEBAR */}
        <div className="support-sidebar">
          <div className="sidebar-header">
            <div className="d-flex align-items-center gap-2">
              <div className="brand-badge-icon">
                <BsShieldCheck />
              </div>
              <h3>Help Desk</h3>
            </div>
            <button className="new-ticket-btn" onClick={() => setShowCreateModal(true)} title="Create New Ticket">
              <BsPlusCircle size={16} />
              <span>New Ticket</span>
            </button>
          </div>

          <div className="ticket-list">
            {loadingTickets ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" size="sm" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="empty-ticket-list">
                <p>No support tickets yet.</p>
              </div>
            ) : (
              tickets.map(t => (
                <div
                  key={t._id}
                  className={`ticket-item ${activeTicket && activeTicket._id === t._id ? 'active' : ''}`}
                  onClick={() => selectTicket(t)}
                >
                  <div className="ticket-item-header">
                    <span className="ticket-id">#{t.ticketId || t._id.substring(18)}</span>
                    <span className={`ticket-status-badge status-${t.status}`}>{t.status}</span>
                  </div>
                  <p className="ticket-subject">{t.subject}</p>
                  <div className="ticket-meta">
                    <span className={`priority-tag priority-${t.priority}`}>{t.priority} priority</span>
                    <span>{new Date(t.lastMessageAt || Date.now()).toLocaleDateString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT CHAT WINDOW */}
        <div className="support-chat-main">
          {activeTicket ? (
            <>
              {/* Header */}
              <div className="chat-window-header">
                <div className="d-flex align-items-center gap-2">
                  <button className="sidebar-toggle-btn d-md-none" onClick={() => setIsSidebarOpen(true)}>
                    <BsList size={22} />
                  </button>

                  <div className="agent-info-box">
                    <div className="agent-avatar">
                      {activeTicket?.assignedAgentId ? activeTicket?.assignedAgentId.Name[0].toUpperCase() : <BsHeadset />}
                    </div>
                    <div className="agent-meta">
                      <h4>{activeTicket.assignedAgentId ? activeTicket.assignedAgentId.Name : 'Support Assistant'}</h4>
                      <span>{activeTicket.assignedAgentId ? 'Assigned Live Agent' : 'Awaiting Agent Assignment'}</span>
                    </div>
                  </div>
                </div>

                <div className="header-actions">
                  {activeTicket.assignedAgentId && activeTicket.status === 'active' && (
                    <button className="header-action-btn call-btn" onClick={startVoiceCall} title="Call Support Agent">
                      <BsTelephone size={16} />
                    </button>
                  )}
                  {activeTicket.status !== 'closed' && (
                    <button className="header-action-btn close-btn" onClick={handleCloseTicket} title="Close Ticket">
                      <BsXCircle size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="messages-container">
                {loadingMessages ? (
                  <div className="text-center my-auto">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  messages.map((m, index) => {
                    const isUser = m.senderType === 'User';
                    return (
                      <div key={m._id || index} className={`message-wrapper ${m.senderType.toLowerCase()}`}>
                        <div className="message-bubble">
                          <span className="sender-badge">{m.senderType}</span>

                          {m.senderType === 'System' ? (
                            <span className="system-msg-text">{m.message}</span>
                          ) : (
                            <>
                              {m.messageType === 'image' && m.attachment && (
                                <div className="message-media">
                                  <img src={`${baseUrl}${m.attachment}`} alt="Attached screenshot" onClick={() => window.open(`${baseUrl}${m.attachment}`, '_blank')} />
                                </div>
                              )}

                              {m.messageType === 'file' && m.attachment && (
                                <div className="message-media py-1">
                                  <a href={`${baseUrl}${m.attachment}`} target="_blank" rel="noreferrer" className="attachment-link">
                                    <BsPaperclip /> View Attachment Document
                                  </a>
                                </div>
                              )}

                              {m.messageType === 'voice' && m.attachment && (
                                <div className="audio-player-wrapper">
                                  <audio src={`${baseUrl}${m.attachment}`} controls />
                                </div>
                              )}

                              {m.message && <p className="message-text">{m.message}</p>}

                              <div className="message-info">
                                <span>{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                {isUser && <BsCheckAll className={`seen-icon ${m.seen ? 'read' : ''}`} />}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}

                {isOpponentTyping && (
                  <div className="typing-indicator-bubble">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Bottom Composer */}
              {activeTicket.status === 'closed' ? (
                <div className="ticket-closed-banner">
                  <span>This support ticket is closed. Please create a new ticket to continue.</span>
                </div>
              ) : (
                <div className="chat-composer">
                  {selectedFile && (
                    <div className="upload-preview-card">
                      <span>📎 Attachment: <strong>{selectedFile.name}</strong></span>
                      <button onClick={() => setSelectedFile(null)}>Remove</button>
                    </div>
                  )}

                  <div className="composer-row">
                    <input type="file" id="composer-file" style={{ display: 'none' }} onChange={handleFileUpload} />
                    <button className="composer-icon-btn attachment-btn" onClick={() => document.getElementById('composer-file').click()} disabled={uploadingFile} title="Attach file">
                      {uploadingFile ? <Spinner size="sm" animation="border" variant="primary" /> : <BsPaperclip size={18} />}
                    </button>

                    {recording ? (
                      <div className="voice-recorder-banner">
                        <div className="voice-recording-status">
                          <div className="recording-dot-blink"></div>
                          <span>Recording ({formatDuration(voiceDuration)})</span>
                        </div>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-danger py-1 px-2" onClick={stopRecording}>Stop</button>
                          <button className="btn btn-sm btn-light border py-1 px-2" onClick={cancelRecording}>Cancel</button>
                        </div>
                      </div>
                    ) : voiceBlob ? (
                      <div className="voice-recorder-banner">
                        <span>Audio Ready</span>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-success py-1 px-2 text-white" onClick={sendVoiceMessage}>Send</button>
                          <button className="btn btn-sm btn-outline-danger py-1 px-2" onClick={() => setVoiceBlob(null)}><BsTrash /></button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          className="composer-input"
                          placeholder="Type your message..."
                          value={inputText}
                          onChange={handleTextInputChange}
                          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                        />

                        <button className="composer-icon-btn mic-btn" onClick={startRecording} title="Record voice message">
                          <BsMic size={18} />
                        </button>
                      </>
                    )}

                    <button className="composer-send-btn" onClick={handleSendMessage} title="Send Message">
                      <BsSend size={15} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="chat-empty-state">
              <div className="empty-support-icon">
                <BsHeadset />
              </div>

              <h2>Customer Support</h2>

              <p>
                We are here to assist you 24/7.
                <br />
                Select an active ticket or open a new support request.
              </p>

              <button
                className="start-support-chat-btn"
                onClick={() => setShowCreateModal(true)}
              >
                <BsPlusCircle size={18} />
                <span>Start New Ticket</span>
              </button>
            </div>
          )}
        </div>

        {/* CALL OVERLAY PANEL */}
        {callActive && (
          <div className="support-call-overlay">
            <div className="call-card">
              <div className="call-avatar">
                <BsFillTelephoneFill size={28} />
              </div>

              {callState === 'calling' && <h4>Calling Support Agent...</h4>}
              {callState === 'ringing' && <h4>Incoming Voice Call</h4>}
              {callState === 'connected' && (
                <>
                  <h4>Call Connected</h4>
                  <div className="call-timer-display">{formatDuration(callDuration)}</div>
                </>
              )}

              <div className="call-actions">
                {callState === 'ringing' && (
                  <>
                    <button className="call-action-circle accept" onClick={acceptCall}>
                      <BsPlayFill size={24} />
                    </button>
                    <button className="call-action-circle decline" onClick={rejectCall}>
                      <BsXCircle size={22} />
                    </button>
                  </>
                )}

                {(callState === 'calling' || callState === 'connected') && (
                  <>
                    <button className="call-action-circle mute" onClick={toggleMute} title={isMuted ? 'Unmute' : 'Mute'}>
                      {isMuted ? <BsMicMute size={20} /> : <BsMic size={20} />}
                    </button>
                    <button className="call-action-circle decline" onClick={hangupCall}>
                      <BsXCircle size={22} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* NEW TICKET MODAL */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered className="custom-gradient-modal">
          <Modal.Header closeButton>
            <Modal.Title>Open Support Ticket</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group mb-3">
              <label className="form-label font-weight-bold">Select Topic</label>
              <select className="form-select custom-modal-input" value={newSubject} onChange={(e) => setNewSubject(e.target.value)}>
                <option value="Profile">Issue in Profile</option>
                <option value="Game">Issue in Game</option>
                <option value="Deposit">Issue in Deposit</option>
                <option value="Withdraw">Issue in Withdraw</option>
                <option value="Referral">Issue in Referral</option>
                <option value="Penalty">Issue in Penalty</option>
                <option value="Bonus">Issue in Bonus</option>
                <option value="Others">Others</option>
              </select>
            </div>
            <div className="form-group mb-3">
              <label className="form-label font-weight-bold">Priority Level</label>
              <select className="form-select custom-modal-input" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" className="border rounded-pill px-4" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button className="btn-gradient-modal rounded-pill px-4" onClick={handleCreateTicket}>Create Ticket</Button>
          </Modal.Footer>
        </Modal>

      </div>
    </div>
  );
};

export default SupportChat;